import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Boxes, Building2, Scale, Cpu, Swords, ShieldAlert, AlertOctagon, ClipboardCheck, Clock, DollarSign, TrendingUp, Gauge, CheckCircle2, Layers } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { StatCard, PageHeader, Loading, Card, CHART_COLORS } from "../components/common";

export default function Dashboard() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/dashboard/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <Loading />;

  const fmt = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(0)}K` : `$${n}`);
  const maturity = ["Initial", "Developing", "Defined", "Managed", "Optimised"][s.governance_maturity - 1] || "Defined";

  const stages = ["Not Started", "Governance", "AI Policy", "Risk Assessment", "Data Governance", "Security", "Model Governance", "Human Approval", "Compliance", "Continuous Monitoring"];
  const stageData = stages.map((st) => ({ name: st.length > 12 ? st.slice(0, 11) + "…" : st, value: (s.stage_distribution.find((x) => x.name === st) || {}).value || 0 }));

  const summary = `Executive dashboard. ${s.total_usecases} synthetic use cases across 20 industries. ${s.open_critical_redteam} open critical red team findings. ${s.open_critical_risks} open critical risks. ${s.pending_approvals} pending approvals. Governance maturity is ${maturity}.`;

  return (
    <div>
      <PageHeader title={t("nav.dashboard")} subtitle={t("subtitle")} summary={summary} />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
        <StatCard testid="stat-usecases" label={t("total_usecases")} value={s.total_usecases} icon={Boxes} onClick={() => nav("/usecases")} />
        <StatCard testid="stat-industries" label={t("industries")} value={s.industries} icon={Building2} onClick={() => nav("/usecases")} />
        <StatCard testid="stat-regcodes" label={t("regulatory_codes")} value={s.regulatory_codes} icon={Scale} onClick={() => nav("/regulatory")} />
        <StatCard testid="stat-models" label={t("ai_models")} value={s.models} icon={Cpu} onClick={() => nav("/models")} />
        <StatCard testid="stat-redteam" label={t("redteam_tests")} value={s.redteam_tests} icon={Swords} onClick={() => nav("/redteam")} />
        <StatCard testid="stat-crit-risk" label={t("open_critical_risks")} value={s.open_critical_risks} icon={ShieldAlert} accent="bg-red-50 text-red-600" onClick={() => nav("/risk")} />
        <StatCard testid="stat-high-risk" label={t("open_high_risks")} value={s.open_high_risks} icon={AlertOctagon} accent="bg-orange-50 text-orange-600" onClick={() => nav("/risk")} />
        <StatCard testid="stat-redteam-findings" label={t("open_redteam")} value={s.open_redteam_findings} icon={Swords} accent="bg-red-50 text-red-600" onClick={() => nav("/redteam")} />
        <StatCard testid="stat-approvals" label={t("pending_approvals")} value={s.pending_approvals} icon={ClipboardCheck} accent="bg-amber-50 text-amber-600" onClick={() => nav("/compliance")} />
        <StatCard testid="stat-gaps" label={t("compliance_gaps")} value={s.compliance_gaps} icon={ClipboardCheck} accent="bg-orange-50 text-orange-600" onClick={() => nav("/compliance")} />
        <StatCard testid="stat-overdue" label={t("overdue_remediations")} value={s.overdue_remediations} icon={Clock} accent="bg-amber-50 text-amber-600" onClick={() => nav("/redteam")} />
        <StatCard testid="stat-production" label="Production AI Systems" value={s.production} icon={CheckCircle2} accent="bg-emerald-50 text-emerald-600" onClick={() => nav("/usecases")} />
        <StatCard testid="stat-cost" label={t("ai_cost")} value={fmt(s.ai_cost)} icon={DollarSign} sub="SYNTHETIC ESTIMATE" onClick={() => nav("/value")} />
        <StatCard testid="stat-value" label={t("ai_value")} value={fmt(s.ai_value)} icon={TrendingUp} sub="SYNTHETIC ESTIMATE" accent="bg-emerald-50 text-emerald-600" onClick={() => nav("/value")} />
        <StatCard testid="stat-readiness" label={t("ai_readiness")} value={`${s.ai_readiness}%`} icon={Gauge} onClick={() => nav("/kpi")} />
        <StatCard testid="stat-maturity" label={t("governance_maturity")} value={maturity} icon={Layers} onClick={() => nav("/kpi")} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card title={t("lifecycle_dist")}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stageData} margin={{ left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#8A1538" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title={t("risk_dist")}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={s.risk_distribution} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                {s.risk_distribution.map((e, i) => <Cell key={i} fill={{ Low: "#2563EB", Medium: "#D97706", High: "#EA580C", Critical: "#DC2626" }[e.name] || CHART_COLORS[i]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title={t("industry_dist")} className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={s.industry_distribution} margin={{ left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={110} interval={0} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#D4AF37" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
